import {
  ConfigurationType,
  RadialDuctEvaluationResultType,
  RadialDuctMetricsType,
  ValidSystemType
} from 'data/types';
import {
  RadialDuctPassiveSpacesByZoneType,
  RadialDuctProductPassiveSpacesType
} from 'utils/air-handler-availability-types';
import {
  DUCT_MODE_KEYS,
  findZoneDuctingEvaluation,
  getZoneDuctingModes,
  RadialDuctModeKeyType,
  resolveZoneDuctModeKey
} from 'utils/ducting-products';

type ConfigurationZoneType =
  ConfigurationType['condensers'][number]['zones'][number];

const AIR_HANDLER_SLOT_COUNT = 10;

function passiveIdsFromMetrics(
  metrics: RadialDuctMetricsType | null | undefined
): Set<string> | null {
  if (!metrics) return null;
  return new Set(
    metrics.rooms
      .filter((room) => room.servedPassively === true)
      .map((room) => room.uniqueId)
  );
}

function intersect(a: Set<string>, b: Set<string>): Set<string> {
  return new Set([...a].filter((id) => b.has(id)));
}

function metricsForStoredMode(
  ducting: RadialDuctEvaluationResultType | null | undefined,
  storedModeKey?: RadialDuctModeKeyType
): RadialDuctMetricsType | null | undefined {
  if (!ducting) return null;
  const modes = getZoneDuctingModes(ducting);
  const preferredKey = resolveZoneDuctModeKey(modes, storedModeKey);
  return (
    (preferredKey ? modes[preferredKey]?.metrics : null) ?? ducting.metrics
  );
}

function passiveIdsAcrossZoneEvaluations(
  zoneUniqueId: string,
  validSystems: ValidSystemType[],
  storedModeKey?: RadialDuctModeKeyType
): Set<string> {
  const seenProductIds = new Set<number>();
  let result: Set<string> | null = null;
  for (const system of validSystems) {
    for (let slot = 1; slot <= AIR_HANDLER_SLOT_COUNT; slot += 1) {
      const uniqueKey = `airHandler${slot}Unique` as keyof ValidSystemType;
      const productKey = `airHandler${slot}ProductId` as keyof ValidSystemType;
      const ductingKey = `zone${slot}Ducting` as keyof ValidSystemType;
      const productId = system[productKey];
      if (
        system[uniqueKey] === zoneUniqueId &&
        typeof productId === 'number' &&
        !seenProductIds.has(productId)
      ) {
        const ducting = system[ductingKey] as
          | RadialDuctEvaluationResultType
          | null
          | undefined;
        const ids = passiveIdsFromMetrics(
          metricsForStoredMode(ducting, storedModeKey)
        );
        if (ids) {
          seenProductIds.add(productId);
          result = result === null ? ids : intersect(result, ids);
          if (result.size === 0) return result;
        }
      }
    }
  }
  return result ?? new Set();
}

// The options endpoint's per-product answer, resolved the same way as a row's
// zoneNDucting: the stored mode when it passes, else the first passing mode,
// falling back to the preferred evaluation's metrics.
function passiveIdsFromProductPassiveSpaces(
  product: RadialDuctProductPassiveSpacesType,
  storedModeKey?: RadialDuctModeKeyType
): Set<string> | null {
  const { modes } = product;
  const hasModes = DUCT_MODE_KEYS.some((key) => modes[key] !== undefined);
  if (hasModes) {
    const resolvedKey =
      storedModeKey && modes[storedModeKey]?.pass
        ? storedModeKey
        : DUCT_MODE_KEYS.find((key) => modes[key]?.pass) ?? null;
    const ids = resolvedKey
      ? modes[resolvedKey]?.passiveSpaceIds
      : product.preferredPassiveSpaceIds;
    const resolved = ids ?? product.preferredPassiveSpaceIds;
    return resolved ? new Set(resolved) : null;
  }
  return product.preferredPassiveSpaceIds
    ? new Set(product.preferredPassiveSpaceIds)
    : null;
}

function passiveIdsAcrossOptionsProducts(
  products: ReadonlyArray<RadialDuctProductPassiveSpacesType>,
  storedModeKey?: RadialDuctModeKeyType
): Set<string> {
  let result: Set<string> | null = null;
  for (const product of products) {
    const ids = passiveIdsFromProductPassiveSpaces(product, storedModeKey);
    if (ids) {
      result = result === null ? ids : intersect(result, ids);
      if (result.size === 0) return result;
    }
  }
  return result ?? new Set();
}

function flaggedPassiveIds(zone: ConfigurationZoneType): Set<string> {
  return new Set(
    (zone.zoneItems ?? [])
      .filter((item) => item.servePassively === true)
      .map((item) => item.structureItemId)
  );
}

function union(a: Set<string>, b: Set<string>): Set<string> {
  return new Set([...a, ...b]);
}

// With no product selected (the whole air-handler step) the answer is the
// intersection across every evaluated concealed product for the zone. The
// options response carries that per product; when it is present it is the
// source, otherwise the fetched rows' zoneNDucting metrics are walked.
function passiveIdsWithoutSelectedProduct(
  zoneUniqueId: string,
  validSystems: ValidSystemType[],
  storedModeKey: RadialDuctModeKeyType | undefined,
  optionsPassiveSpaces: RadialDuctPassiveSpacesByZoneType | null | undefined
): Set<string> {
  const products = optionsPassiveSpaces?.[zoneUniqueId];
  if (products) {
    return passiveIdsAcrossOptionsProducts(products, storedModeKey);
  }
  return passiveIdsAcrossZoneEvaluations(
    zoneUniqueId,
    validSystems,
    storedModeKey
  );
}

export function getPassiveSpaceIdsForZone(
  zone: ConfigurationZoneType,
  validSystems: ValidSystemType[],
  storedModeKey?: RadialDuctModeKeyType,
  optionsPassiveSpaces?: RadialDuctPassiveSpacesByZoneType | null
): Set<string> {
  const flagged = flaggedPassiveIds(zone);
  const productId = zone.selectedProduct?.productId;
  if (!productId) {
    return union(
      flagged,
      passiveIdsWithoutSelectedProduct(
        zone.uniqueId,
        validSystems,
        storedModeKey,
        optionsPassiveSpaces
      )
    );
  }
  const ducting = findZoneDuctingEvaluation(
    validSystems,
    zone.uniqueId,
    productId
  );
  if (!ducting) {
    return union(
      flagged,
      passiveIdsWithoutSelectedProduct(
        zone.uniqueId,
        validSystems,
        storedModeKey,
        optionsPassiveSpaces
      )
    );
  }
  return union(
    flagged,
    passiveIdsFromMetrics(metricsForStoredMode(ducting, storedModeKey)) ??
      new Set<string>()
  );
}
